"use client";

/**
 * AutopilotCreatorCard — one card in the Autopilot tab's horizontal row.
 *
 * Renders a single curated "featured" alert: a GPT-5.4-generated
 * TradingView-style thumbnail (4:3) at the top + a footer bar with the
 * alert title, creator avatar + handle, subscriber count, and schedule
 * chip.
 *
 * Visual language mirrors:
 *   - `AutomationsTutorialCard`  — rounded-2xl frame + cyan glow
 *                                  that sits next to this card.
 *   - `CaseStudyCard`            — dark panel + subtle hover lift.
 *   - `FeedCard`                 — avatar + @handle + tiny meta row.
 *
 * Click behavior: navigates to `/alerts/{alert_id}` using next/link so
 * the browser does a client-side route change with prefetching (no full
 * page reload, snappy on hover).
 *
 * Performance:
 *   - Image via <Image unoptimized> pointing at the GCS public URL so
 *     we don't round-trip through the Next image proxy. Thumbnails are
 *     already sized right by GPT-5.4 (4:3, ~1024px wide) so no resize
 *     is needed.
 *   - Zero side-effects / no hooks firing on scroll — this is a pure
 *     presentational component. All data fetching happens in the
 *     parent (feed.tsx).
 */

import Link from "next/link";
import { useEffect } from "react";
import { CheckCircle2, Clock, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { createChannelLogger } from "@/lib/debug-log";
import type { FeaturedAlertItem } from "@/lib/api-client";
import {
  formatAutomationCount,
  getDisplaySubscriberCount,
} from "@/lib/automations-display";

const log = createChannelLogger("autopilot");

interface AutopilotCreatorCardProps {
  item: FeaturedAlertItem;
  className?: string;
}

export function AutopilotCreatorCard({
  item,
  className,
}: AutopilotCreatorCardProps) {
  const creator = item.presented_by; // always kind="creator" for this route
  const schedule = item.schedule_description || item.schedule || "Scheduled";
  const subscribers = formatAutomationCount(getDisplaySubscriberCount(item));

  useEffect(() => {
    log("card:mount", {
      alert_id: item.alert_id,
      name: item.name,
      thumb: item.thumbnail_url,
      handle: creator?.handle,
    });
  }, [item.alert_id, item.name, item.thumbnail_url, creator?.handle]);

  return (
    <Link
      href={`/alerts/${item.alert_id}`}
      prefetch
      className={cn(
        "group relative shrink-0 snap-start text-left rounded-2xl overflow-hidden flex flex-col",
        // Match AutomationsTutorialCard sky/cyan palette so the row
        // reads as a cohesive set. Slightly softer glow than the
        // onboarding card to keep the Tutorial card dominant.
        "border border-white/[0.06] bg-[#0c0c0e]",
        "shadow-[0_2px_12px_rgba(0,0,0,0.5)]",
        "transition-[transform,border-color,box-shadow] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]",
        "hover:border-sky-400/30 hover:shadow-[0_8px_32px_rgba(56,189,248,0.10)] hover:-translate-y-0.5",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/50",
        className,
      )}
      aria-label={`Open ${item.name} by @${creator.handle}`}
    >
      {/* Thumbnail: 16:9 to match the Blogs + Community cards across all
       *  three home tabs. No overlays — they crowd the image. */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#131722] shrink-0">
        {/* No overlay tag here: the GPT thumbnail already bakes its own asset
         *  chip + ALERT pill in the top corners, so an extra pill just covers
         *  the title. Subscriber count sits at the BOTTOM to keep the top
         *  band (where the baked title lives) completely clear. */}
        <div className="absolute bottom-2 left-3 z-10">
          <div className="flex items-center gap-1 rounded-md border border-white/15 bg-black/65 px-2 py-1 backdrop-blur-md">
            <Users className="h-3 w-3 text-zinc-300" strokeWidth={2.4} />
            <span className="text-[11px] font-semibold tracking-wide text-zinc-200">
              {subscribers}
            </span>
          </div>
        </div>
        {/* Plain <img> instead of next/image — we set `unoptimized`
         *  anyway (these thumbnails come from GCS already sized right),
         *  and Next's `remotePatterns` gate was silently crashing the
         *  card when the creator avatar host wasn't whitelisted. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={item.thumbnail_url}
          alt={item.name}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          onLoad={() =>
            log("card:image:load", {
              alert_id: item.alert_id,
              thumb: item.thumbnail_url,
            })
          }
          onError={() =>
            log("card:image:error", {
              alert_id: item.alert_id,
              thumb: item.thumbnail_url,
            })
          }
        />
      </div>

      {/* Footer: one-line title + one meta row, matching the Blogs +
       *  Community cards so all three home tabs share one layout. */}
      <div className="relative px-4 pt-3.5 pb-4 flex-1 flex flex-col gap-2.5">
        <h3 className="line-clamp-1 text-[14.5px] font-semibold leading-snug text-slate-50">
          {item.name}
        </h3>

        {/* Creator + Schedule */}
        <div className="mt-auto flex items-center justify-between gap-3">
          {/* Left: Avatar + Handle (truncated gracefully to leave room for schedule) */}
          <div className="flex items-center gap-2 min-w-0 max-w-[50%]">
            <div className="relative h-6 w-6 shrink-0 rounded-full overflow-hidden border border-zinc-700 bg-zinc-900 shadow-sm">
              {creator.avatar_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={creator.avatar_url}
                  alt={creator.display_name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-zinc-400">
                  {creator.display_name?.slice(0, 1) || "?"}
                </div>
              )}
            </div>
            <div className="flex min-w-0 items-center gap-1 text-[12px] font-medium text-slate-200">
              <span className="truncate">@{creator.handle}</span>
              {creator.verified && (
                <CheckCircle2
                  className="h-3.5 w-3.5 shrink-0 text-sky-400"
                  strokeWidth={2.4}
                  aria-label="Verified"
                />
              )}
            </div>
          </div>

          {/* Right: Schedule (given more room, truncated if absolutely needed) */}
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-400 min-w-0">
            <Clock className="h-3 w-3 shrink-0" strokeWidth={2.2} />
            <span className="truncate">{schedule}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * Skeleton placeholder while the fetch is in flight. Same outer shape
 * as the real card so layout doesn't shift when data arrives.
 */
export function AutopilotCreatorCardSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative shrink-0 rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0c0c0e] animate-pulse flex flex-col",
        className,
      )}
    >
      <div className="aspect-[16/9] w-full bg-zinc-900/60 shrink-0" />
      <div className="px-4 pt-3.5 pb-4 flex-1 flex flex-col gap-2.5">
        <div className="h-4 w-3/4 rounded bg-zinc-800/70" />
        <div className="mt-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-zinc-800/70 shrink-0" />
            <div className="h-3.5 w-24 rounded bg-zinc-800/70" />
          </div>
          <div className="h-3.5 w-20 rounded bg-zinc-800/70 shrink-0" />
        </div>
      </div>
    </div>
  );
}
